<template>
  <b-container fluid>
    <page-header title="Event Configuration">
      <template #actions>
        <b-input-group style="width: 300px;">
          <select class="form-control" v-model="filter.mcevAssetId">
            <option :value="null" disabled>-- Select a device to view data --</option>
            <option v-for="device in devices" :key="device.devcId" :value="device.devcUniqueId.replace('gateway-', '')">
              {{ device.devcMass.massName }} ({{ device.devcUniqueId.replace('gateway-', '') }})
            </option>
          </select>
        </b-input-group>
        <button-options :access="'master_config_event'" :hideAdd="!filter.mcevAssetId" :hideDetail="true"
          :isRefreshed="isRefreshed" @onAddItemClicked="openAddForm"
          class="mt-0" />
      </template>
    </page-header>
    <div class="iq-card p-4">
      <div class="iq-card-body mt-4">
        <div v-if="filter.mcevAssetId">
          <b-table striped hover fixed show-empty responsive stacked="md" :busy="isLoading" :fields="fields"
            :items="canRead('master_config_event') ? data : []" style="border-radius: 10px;">
            <!-- Start table busy slot -->
            <template v-slot:table-busy="">
              <div class="text-center text-danger my-2">
                <wave-loader />
              </div>
            </template>
            <!-- End table busy slot -->

            <!-- Start isActive slot -->
            <template #cell(mcevIsActive)="data">
              <b-badge :variant="data.value ? 'success' : 'danger'">
                {{ data.value ? 'Yes' : 'No' }}
              </b-badge>
            </template>
            <!-- End isActive slot -->

             <!-- Start description slot -->
            <template #cell(mcevDescription)="data">
                <span v-if="!data.item.mcevDescription">-</span>
                <span v-else-if="data.item.mcevDescription.length <= 50">
                    {{ data.item.mcevDescription }}
                </span>
                <a href="#" v-else @click.prevent="showFullDescription(data.item.mcevDescription)">
                    {{ data.item.mcevDescription.substring(0, 50) + '...' }}
                </a>
            </template>
            <!-- End description slot -->

            <!-- Start createdTime slot -->
            <template v-slot:cell(matyCreatedTime)="data">
              {{ dateWithDDMMYYYYFormat(data.value) }}
            </template>
            <!-- End createdTime slot -->

            <!-- Start action slot -->
            <template v-slot:cell(action)="data">
              <div class="text-left d-flex">
                <button-options :access="'master_config_event'" :hideAdd="true" :hideDelete="data.item.mcevIsActive == 1"
                  :hideDetail="false" :hideEdit="false" @onDeleteClicked="openDelete(data.item)"
                  @onEditClicked="openEdit(data.item)" @onViewClicked="openDetail(data.item)" />
                <template v-if="data.item.mcevIsActive == 0">
                    <b-button
                        v-if="activationConfirmationId !== data.item.mcevId"
                        variant="primary"
                        size="sm"
                        class="ml-1"
                        @click="onActivateClicked(data.item)"
                    >
                        Activate
                    </b-button>
                    <b-button
                        v-else
                        variant="warning"
                        size="sm"
                        class="ml-1"
                        @click="onConfirmActivate(data.item)"
                    >
                        Confirm?
                    </b-button>
                </template>
              </div>
            </template>
            <!-- End action slot -->

          </b-table>
          <b-pagination v-model="pagination.current" :per-page="pagination.pageSize" :total-rows="pagination.totalSize"
            @change="onPageChanged"></b-pagination>
        </div>
        <div v-else class="text-center p-5">
          <h5>Please select a device from the filter above to view its event configurations.</h5>
        </div>
      </div>
      <b-modal size="lg" hide-footer no-close-on-esc no-close-on-backdrop v-model="displayForm">
        <!-- Start Form -->
        <div class="iq-card mb-0" style="min-width: 300px;">
          <div class="iq-card-header d-flex justify-content-between">
            <div class="iq-header-title">
              <h4 class="card-title" v-if="!current.mcevId">Add Event</h4>
              <h4 class="card-title" v-else>Edit Event</h4>
            </div>
          </div>
          <div class="iq-card-body">
            <div class="row">
              <!-- asset ID input -->
              <div role="group" class="form-group mb-3 col-md-6">
                <label class="d-block form-label text-left">
                  Asset ID:
                </label>

                <div class="bv-no-focus-ring">
                  <div role="group" class="input-group">
                    <select class="form-control" v-model="current.mcevAssetId"
                      :class="errors.mcevAssetId ? 'is-invalid' : ''" disabled>
                      <option :value="null" disabled>-- Please select a device --</option>
                      <option v-for="device in devices" :key="device.devcId" :value="device.devcUniqueId.replace('gateway-', '')">
                        {{ device.devcMass.massName }} ({{ device.devcUniqueId }})
                      </option>
                    </select>
                    <b-form-invalid-feedback v-if="errors.mcevAssetId">
                      {{ Object.values(errors.mcevAssetId) }}
                    </b-form-invalid-feedback>
                  </div>
                </div>
              </div>
              <!-- end asset ID input -->

              <!-- version input -->
              <div role="group" class="form-group mb-3 col-md-6">
                <label class="d-block form-label text-left">
                  Version:
                </label>

                <div class="bv-no-focus-ring">
                  <div role="group" class="input-group">
                    <NumberInput class="form-control" :decimals="0" :grouping="false" placeholder="Version"
                      v-model="current.mcevVersion" :class="errors.mcevVersion ? 'is-invalid' : ''" disabled />
                    <b-form-invalid-feedback v-if="errors.mcevVersion">
                      {{ Object.values(errors.mcevVersion) }}
                    </b-form-invalid-feedback>
                  </div>
                </div>
              </div>
              <!-- end version input -->

              <!-- description input -->
              <div role="group" class="form-group mb-3 col-md-12">
                <label class="d-block form-label text-left">
                  Description:
                </label>

                <div class="bv-no-focus-ring">
                  <div role="group" class="input-group">
                    <textarea class="form-control" style="height: 100px;" placeholder="Description"
                      v-model="current.mcevDescription" :class="errors.mcevDescription ? 'is-invalid' : ''"></textarea>
                    <b-form-invalid-feedback v-if="errors.mcevDescription">
                      {{ Object.values(errors.mcevDescription) }}
                    </b-form-invalid-feedback>
                  </div>
                </div>
              </div>
              <!-- end description input -->
            </div>

            <div class="form-group">
              <b-button @click="onCancel" class="mr-1" size="sm" :disabled="isSubmit" variant="outline-danger">
                <div class="mx-2">Cancel</div>
              </b-button>
              <b-button type="submit" @click="onSubmitClicked(current)" :disabled="isSubmit" variant="primary" size="sm"
                class="ml-1">
                <div class="mx-2">
                  <wave-loader v-if="isSubmit" size="sm" mono />
                  Submit
                </div>
              </b-button>
            </div>

          </div>
        </div>
        <!-- End Form -->
      </b-modal>
      <b-modal size="lg" hide-footer no-close-on-esc no-close-on-backdrop v-model="displayDeletion">
        <delete-item :isSubmit="isSubmit" @onCancelDeletion="displayDeletion = false"
          @onConfirmDeletion="onConfirmDeletion" />
      </b-modal>
       <b-modal title="Full Description" hide-footer v-model="displayDescriptionModal">
        <p style="word-wrap: break-word;">{{ fullDescription }}</p>
        <b-button class="mt-3" block @click="displayDescriptionModal = false">Close</b-button>
     </b-modal>
    </div>
  </b-container>
</template>

<script>

import DeleteItem from '@/components/directives/delete-item';
import ButtonOptions from '@/components/directives/button-options';
import EventSvc from '@/services/ConfigEventSvc';
import DeviceSvc from '@/services/DeviceSvc';
import AssetEventSvc from '@/services/AssetEventSvc';

import requestBuilder from '@/util/master/request.builder';
import NumberInput from '@/components/directives/NumberInput.vue';

export default {
  name: 'MasterConfigEvent',
  components: {
    NumberInput,
    DeleteItem,
    ButtonOptions,
  },
  data() {
    return {
      current: {},
      data: [],
      devices: [],
      displayDeletion: false,
      displayForm: false,
      displayDescriptionModal: false,
      fullDescription: '',
      errors: {},
      filter: {
        mcevAssetId: null,
      },
      isLoading: false,
      isRefreshed: false,
      isSubmit: false,
      activationConfirmationId: null,
      pagination: {
        current: 1,
        pageSize: 10,
        totalSize: 0,
      },
      fields: [
        {
          key: 'mcevAssetId',
          label: 'Asset ID',
          class: 'text-left',
        },
        {
          key: 'mcevVersion',
          label: 'Version',
          class: 'text-left',
        },
        {
          key: 'mcevDescription',
          label: 'Description',
          class: 'text-left',
        },
        {
          key: 'mcevIsActive',
          label: 'Is Active',
          class: 'text-left',
        },
        {
          key: 'mcevAppliedTime',
          label: 'Applied Time',
          class: 'text-left',
        },
        {
          key: 'action',
          label: '',
          class: 'text-left',
          thStyle: { minWidth: '300px' },
        },
      ],
    };
  },
  watch: {
    'filter.mcevAssetId': {
      handler(newVal) {
        if (newVal) {
          if (newVal !== this.$route.params.id) {
            this.$router.replace({ name: 'MasterConfigEvent', params: { id: newVal } });
          }
          this.activationConfirmationId = null;
          this.pagination.current = 1;
          this.getData('Device selected');
        }
      },
    },
  },
  methods: {
    async deleteData(notification, data) {
      this.isSubmit = true;
      try {
        const deleteResponse = await EventSvc.delete(data.mcevId);
        if (deleteResponse.status.error === 0) {
          this.$toast.success(notification);
          this.displayDeletion = false;
          this.pagination.current = 1;
          this.getData();
        } else {
          this.$toast.error(deleteResponse.status.message);
        }
      } catch (e) {
        this.$toast.error(e.message);
      } finally {
        this.isSubmit = false;
      }
    },
    async getData(notification) {
      if (!this.filter.mcevAssetId) {
        this.$toast.error('Please select an Asset ID to display data.');
        return;
      }
      try {
        this.isLoading = true;
        this.isRefreshed = true;
        const params = {
          ...requestBuilder.buildFilters(this.pagination, this.filter),
          'orders[mcevVersion]': 'desc',
        };
        const Response = await EventSvc.get(params);

        const Data = Response.data;

        this.data = Data || [];

        this.pagination.totalSize = Response.pagination?.totalData || 0;
        if (notification) {
          this.$toast.success(notification);
        }
      } catch (e) {
        console.error('get data error : ', e);
        this.$toast.error('Something Wrong');
      } finally {
        this.isLoading = false;
        this.isRefreshed = false;
      }
    },
    async getDevices() {
      try {
        const config = {
          params: {
            max: 1000,
            'finds[devcType]': 'gateway',
          },
        };
        const response = await DeviceSvc.getDevices(config);
        if (response.data && response.data.data) {
          this.devices = response.data.data;
        }
      } catch (e) {
        console.error('Failed to fetch devices:', e);
        this.$toast.error('Could not load device list.');
      }
    },
    onCancel() {
      this.current = {};
      this.displayForm = false;
    },
    onConfirmDeletion() {
      this.deleteData('Event Deleted', this.selected);
    },
    onPageChanged(page) {
      this.pagination.current = page;
      this.getData();
    },
    onUpdated() {
      this.getData('Updated');
    },
    onActivateClicked(item) {
      this.activationConfirmationId = item.mcevId;
    },
    async onConfirmActivate(item) {
      this.isSubmit = true;
      try {
        const assetEventResponse = await AssetEventSvc.get({
          'finds[asevMcevId]': item.mcevId,
          max: 1,
        });

        if (assetEventResponse.pagination.totalData === 0) {
          this.$toast.error('Cannot activate. Please add events to this configuration first.');
          return;
        }

        const payload = {
          mcevAssetId: item.mcevAssetId,
          mcevVersion: item.mcevVersion,
          mcevDescription: item.mcevDescription,
          mcevIsActive: '1',
        };
        const updateResponse = await EventSvc.update(item.mcevId, payload);
        if (updateResponse.status.error === 0) {
          this.$toast.success('Event activated successfully');
          await this.getData();
        } else {
          this.$toast.error(updateResponse.status.message || 'Error activating event');
        }
      } catch (e) {
        this.$toast.error(e.message);
      } finally {
        this.isSubmit = false;
        this.activationConfirmationId = null;
      }
    },
    async onSubmitClicked(form) {
      try {
        this.errors = {};
        this.isSubmit = true;

        const formData = {
          mcevAssetId: form.mcevAssetId,
          mcevVersion: form.mcevVersion,
          mcevDescription: form.mcevDescription,
          mcevIsActive: form.mcevIsActive,
        };

        if (!form.mcevId) { // jika tidak ada mcevId maka create
          const createResponse = await EventSvc.create(formData);

          if (createResponse.status.error === 0) {
            await this.getData('Success');
            this.current = {};
          } else {
            this.$toast.error('Error when saving data');
          }
        } else { // jika ada mcevId maka update
          delete formData.mcevIsActive;
          const updateResponse = await EventSvc.update(form.mcevId, formData);
          console.debug('updateResponse : ', updateResponse);

          if (updateResponse.status.error === 0) {
            await this.getData('Success');
            this.current = {};
          } else {
            this.$toast.error('Error when Update data');
          }
        }
      } catch (e) {
        console.error('onSubmitClicked error : ', e);
        this.$toast.error('Error When Submiting Form');
      } finally {
        this.displayForm = false;
        this.isSubmit = false;
      }
    },
    openAddForm() {
      if (!this.filter.mcevAssetId) {
        this.$toast.error('Please select an Asset ID from the filter before adding a new event.');
        return;
      }
      let nextVersion = 1;
      if (this.data && this.data.length > 0) {
        const lastVersion = this.data[0].mcevVersion;
        nextVersion = lastVersion + 1;
      }
      this.current = {
        mcevAssetId: this.filter.mcevAssetId,
        mcevVersion: nextVersion,
        mcevIsActive: '0',
      };
      this.displayForm = true;
    },
    openDelete(data) {
      this.activationConfirmationId = null;
      this.displayDeletion = true;
      this.selected = data;
    },
    openDetail(data) {
      this.$router.push({ name: 'AssetEvent', params: { id: data.mcevId } });
    },
    openEdit(data) {
      this.activationConfirmationId = null;
      this.displayForm = true;
      const currentData = { ...this._.find(this.data, (v) => v.mcevId === data.mcevId) };
      if (currentData.mcevAssetId) {
        currentData.mcevAssetId = currentData.mcevAssetId.replace('gateway-', '');
      }
      this.current = {
        ...currentData,
      };
    },
    showFullDescription(description) {
      this.fullDescription = description;
      this.displayDescriptionModal = true;
    },
    doLogOut() {
      this.$toast.error('Please re-login');
      this.$store.dispatch('auth/logout');
      this.$router.replace({ name: 'Login' });
    },
  },
  mounted() {
    this.getDevices();
    if (this.$route.params.id) {
      this.filter.mcevAssetId = this.$route.params.id;
    }
  },
};
</script>
